import { Match } from '../models';
import { MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { GenerateSwissBracketUseCase } from './GenerateSwissBracketUseCase';
import { logger } from '@/src/core/utils';

export class UpdateMatchResultUseCase {
  private generateSwissUseCase?: GenerateSwissBracketUseCase;

  constructor(
    private matchRepository: MatchRepository,
    private tournamentRepository: TournamentRepository,
    participantRepository?: ParticipantRepository
  ) {
    if (participantRepository) {
      this.generateSwissUseCase = new GenerateSwissBracketUseCase(
        tournamentRepository,
        participantRepository,
        matchRepository
      );
    }
  }

  async execute(params: {
    matchId: string;
    winnerId: string;
    scoreParticipant1?: number;
    scoreParticipant2?: number;
  }): Promise<Match> {
    const { matchId, winnerId, scoreParticipant1, scoreParticipant2 } = params;

    logger.debug('UpdateMatchResult', 'Starting match update', { matchId, winnerId });

    // 1. Get the match
    const match = await this.matchRepository.getById(matchId);
    if (!match) {
      throw new Error('Match not found');
    }

    logger.debug('UpdateMatchResult', 'Match found', {
      bracketType: match.bracketType,
      round: match.round,
      p1: match.participant1Id,
      p2: match.participant2Id
    });

    // 2. Validate that the match has both participants
    if (!match.participant1Id || !match.participant2Id) {
      throw new Error('Match does not have both participants assigned');
    }

    // 3. Validate that winnerId is one of the participants
    if (winnerId !== match.participant1Id && winnerId !== match.participant2Id) {
      throw new Error('Winner must be one of the match participants');
    }

    // 4. Validate that the match is not already completed
    if (match.status === 'completed') {
      throw new Error('Match is already completed');
    }

    // 5. Determine loser
    const loserId = winnerId === match.participant1Id ? match.participant2Id : match.participant1Id;

    logger.debug('UpdateMatchResult', 'Determined winner and loser', { winnerId, loserId });

    // 6. Update the current match
    const updatedMatch = await this.matchRepository.update(matchId, {
      winnerId,
      scoreParticipant1,
      scoreParticipant2,
      status: 'completed',
    });

    logger.debug('UpdateMatchResult', 'Match updated successfully');

    // 7. Handle Grand Final cases
    if (match.bracketType === 'grand_final') {
      if (match.round === 1) {
        logger.debug('UpdateMatchResult', 'Handling Grand Final Round 1');
        await this.handleGrandFinalRound1(match, winnerId, loserId);
      } else if (match.round === 2) {
        logger.info('UpdateMatchResult', 'Grand Final Round 2 - Tournament completed', { tournamentId: match.tournamentId });
        await this.tournamentRepository.update(match.tournamentId, {
          status: 'completed',
        });
      }
    } else {
  logger.debug('UpdateMatchResult', 'Normal progression');
  // 8. Normal progression: Propagate winner and loser
  if (match.nextMatchId) {
    logger.debug('UpdateMatchResult', 'Propagating winner to next match', { nextMatchId: match.nextMatchId });
    await this.propagateWinnerToNextMatch(match.nextMatchId, winnerId);
  } else {
    // No next match means this is the final match
    logger.debug('UpdateMatchResult', 'No next match - checking if tournament is complete');
    const tournament = await this.tournamentRepository.getById(match.tournamentId);

    // For Single Elimination, mark as completed when final match is done
    if (tournament && tournament.tournamentType === 'single_elimination' && match.bracketType === 'winners') {
      logger.info('UpdateMatchResult', 'Single Elimination final match - Tournament completed', { tournamentId: match.tournamentId });
      await this.tournamentRepository.update(match.tournamentId, {
        status: 'completed',
      });
    }

    // For King of the Hill, mark as completed when the last match in the chain is done
    if (tournament && tournament.tournamentType === 'king_of_the_hill') {
      logger.info('UpdateMatchResult', 'King of the Hill final match - Tournament completed', { tournamentId: match.tournamentId });
      await this.tournamentRepository.update(match.tournamentId, {
        status: 'completed',
      });
    }

    // For Round Robin, check if all matches are completed
    if (tournament && tournament.tournamentType === 'round_robin') {
      const allMatches = await this.matchRepository.getByTournamentId(match.tournamentId);
      const allCompleted = allMatches.every(m => m.status === 'completed');

      if (allCompleted) {
        logger.info('UpdateMatchResult', 'Round Robin - All matches complete - Tournament completed', { tournamentId: match.tournamentId });
        await this.tournamentRepository.update(match.tournamentId, {
          status: 'completed',
        });
      }
    }

    // For Swiss, check if current round is completed and generate next round
    if (tournament && tournament.tournamentType === 'swiss' && this.generateSwissUseCase) {
      logger.debug('UpdateMatchResult', 'Swiss tournament - checking if round is complete');
      try {
        await this.generateSwissUseCase.generateNextRound(match.tournamentId);
      } catch (error: any) {
        logger.debug('UpdateMatchResult', 'Swiss next round generation result', { message: error.message });
        // If no next round (tournament complete), it's expected
      }
    }
  }

  if (match.loserNextMatchId) {
    logger.debug('UpdateMatchResult', 'Propagating loser to next bracket', { loserNextMatchId: match.loserNextMatchId });
    await this.propagateLoserToNextMatch(match.loserNextMatchId, loserId);
  }
}

    return updatedMatch;
  }

  private async handleGrandFinalRound1(
    grandFinal: Match,
    winnerId: string,
    loserId: string
  ): Promise<void> {
    try {
      // Determine who came from winners and who from losers
      const allMatches = await this.matchRepository.getByTournamentId(grandFinal.tournamentId);

      // Find the winners bracket final
      const winnersFinal = allMatches.find(
        m => m.bracketType === 'winners' &&
             m.nextMatchId === grandFinal.id &&
             m.status === 'completed'
      );

      // Find the losers bracket final (works for both 'losers' in DE and 'second_losers' in TE)
      const losersFinal = allMatches.find(
        m => m.bracketType !== 'winners' &&
             m.bracketType !== 'grand_final' &&
             m.nextMatchId === grandFinal.id &&
             m.status === 'completed'
      );

      if (!winnersFinal || !losersFinal) {
        logger.error('UpdateMatchResult', 'Could not find bracket finals for grand final');
        throw new Error('Could not determine bracket origins for grand final');
      }

      const winnersChampion = winnersFinal.winnerId;
      const losersChampion = losersFinal.winnerId;

      logger.debug('UpdateMatchResult', 'Grand final participants identified', { winnersChampion, losersChampion });

      // Case 1: Winner came from Winners Bracket → Tournament is over
      if (winnerId === winnersChampion) {
        logger.info('UpdateMatchResult', 'Winners champion won - Tournament completed', { tournamentId: grandFinal.tournamentId });
        await this.tournamentRepository.update(grandFinal.tournamentId, {
          status: 'completed',
        });
        return;
      }

      // Case 2: Winner came from Losers Bracket → Bracket Reset!
      if (winnerId === losersChampion) {
        logger.info('UpdateMatchResult', 'Grand final bracket reset', { matchId: grandFinal.id });
        const grandFinalReset = await this.matchRepository.create({
          tournamentId: grandFinal.tournamentId,
          round: 2,
          matchNumber: 1,
          bracketType: 'grand_final',
          participant1Id: winnersChampion,
          participant2Id: losersChampion,
          status: 'pending',
        });

        await this.matchRepository.update(grandFinal.id, {
          nextMatchId: grandFinalReset.id,
        });

        logger.debug('UpdateMatchResult', 'Bracket reset match created', { resetMatchId: grandFinalReset.id });
        return;
      }
    } catch (error) {
      logger.error('UpdateMatchResult', 'Failed to handle grand final', { error });
      throw error;
    }
  }

  private async propagateWinnerToNextMatch(nextMatchId: string, winnerId: string): Promise<void> {
    try {
      const nextMatch = await this.matchRepository.getById(nextMatchId);
      if (!nextMatch) {
        logger.debug('UpdateMatchResult', 'Next match not found for winner propagation', { nextMatchId });
        return;
      }

      // Assign winner to next match
      if (!nextMatch.participant1Id) {
        logger.debug('UpdateMatchResult', 'Assigning winner to participant1 slot');
        await this.matchRepository.update(nextMatchId, {
          participant1Id: winnerId,
        });
      } else if (!nextMatch.participant2Id) {
        logger.debug('UpdateMatchResult', 'Assigning winner to participant2 slot');
        await this.matchRepository.update(nextMatchId, {
          participant2Id: winnerId,
        });
      } else {
        logger.debug('UpdateMatchResult', 'Both participants already assigned in next match');
      }
    } catch (error) {
      logger.error('UpdateMatchResult', 'Failed to propagate winner', { error });
      throw error;
    }
  }

  private async propagateLoserToNextMatch(loserMatchId: string, loserId: string): Promise<void> {
    try {
      const loserMatch = await this.matchRepository.getById(loserMatchId);
      if (!loserMatch) {
        logger.debug('UpdateMatchResult', 'Loser match not found for propagation', { loserMatchId });
        return;
      }

      // Assign loser to losers bracket match
      if (!loserMatch.participant1Id) {
        logger.debug('UpdateMatchResult', 'Assigning loser to participant1 slot');
        await this.matchRepository.update(loserMatchId, {
          participant1Id: loserId,
        });
      } else if (!loserMatch.participant2Id) {
        logger.debug('UpdateMatchResult', 'Assigning loser to participant2 slot');
        await this.matchRepository.update(loserMatchId, {
          participant2Id: loserId,
        });
      } else {
        logger.debug('UpdateMatchResult', 'Both participants already assigned in loser match');
      }
    } catch (error) {
      logger.error('UpdateMatchResult', 'Failed to propagate loser', { error });
      throw error;
    }
  }
}
